/**Import react section */
import React, { useState, useEffect } from 'react'
import moment from 'moment'
/**Import Material UI section */
import { Stepper, Step, StepLabel, Typography, StepContent, Grid, makeStyles, Chip, Card, CardContent, Link } from '@material-ui/core'
/**Import Icons */
import { Email, Send, Person, Schedule, AccountBox, AttachFile } from '@material-ui/icons'
import { GetAccountType } from '../../../store/helpers/SelectOptions'
import { useSelector } from 'react-redux'
import RichTextEditor from '../../common/RichTextEditor'
import { containsHtmlContent, stripHtmlTags } from '../../../store/helpers/StringHelper'

const useStepIconStyles = makeStyles((theme) => ({
	customStepIcon: {
		backgroundColor: theme.palette.secondary.main,
		color: theme.palette.common.white,
		width: 24,
		height: 24,
		borderRadius: '50%',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		fontSize: '0.75rem',
		fontWeight: 'bold'
	},
	active: {
		backgroundColor: theme.palette.secondary.dark
	},
	completed: {
		backgroundColor: theme.palette.secondary.main
	}
}))

const CustomEmailStepIcon = (props) => {
	const classes = useStepIconStyles()
	const { active, completed } = props

	return (
		<div className={`${classes.customStepIcon} ${active ? classes.active : ''} ${completed ? classes.completed : ''}`}>
			<Email style={{ fontSize: '14px' }} />
		</div>
	)
}
const getAccountTypeLabel = (typeValue) => {
	const accountTypes = GetAccountType()
	const accountType = accountTypes.find((type) => type.value === typeValue)
	const result = accountType ? accountType.label : 'Undefined'
	return result
}

const TimeLineEmailHistory = (props) => {
	const { emailsHistory = [], service } = props
	const [activeStep, setActiveStep] = useState(0)
	const [emails, setEmails] = useState([])
	const user = useSelector((state) => state.oidc.user)

	const handleAttachmentClick = (emailToken, attachmentToken) => {
		const url = `${window.config.serviceManagementBackendUrl}/services/email-history/${emailToken}/attachment/${attachmentToken}`
		fetch(url, {
			headers: { Authorization: `Bearer ${user.access_token}` }
		})
			.then((response) => response.blob())
			.then((blob) => {
				const blobUrl = URL.createObjectURL(blob)
				window.open(blobUrl, '_blank')
			})
			.catch((error) => console.error('Error downloading attachment', error))
	}

	useEffect(() => {
		setEmails(emailsHistory.reverse())
	}, [emailsHistory])

	const onClickShowItem = (event, stepNumber) => {
		event.stopPropagation()
		setActiveStep((prevActiveStep) => stepNumber)
	}

	const formatEmailPreview = (message, maxLength = 1000) => {
		const plainText = stripHtmlTags(message)
		return plainText.length > maxLength ? plainText.substring(0, maxLength) + '...' : plainText
	}

	const getStatusColor = (status) => {
		switch (status.toLowerCase()) {
			case 'sent':
				return 'primary'
			case 'delivered':
				return 'primary'
			case 'pending':
				return 'default'
			case 'failed':
				return 'secondary'
			case 'bounced':
				return 'secondary'
			default:
				return 'default'
		}
	}

	return (
		<div>
			{emails && emails.length > 0 ? (
				<>
					<Typography variant='h6' style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
						<Email style={{ marginRight: '8px' }} />
						Email History
					</Typography>
					<Typography variant='caption' color='textSecondary' style={{ marginBottom: '16px' }}>
						Time zone in Mexico City, CDMX, Mexico (GMT-6)
					</Typography>
				</>
			) : (
				<Typography variant='h6' color='textSecondary'>
					No email history available
				</Typography>
			)}

			{emails && emails.length > 0 && (
				<Stepper activeStep={activeStep} orientation='vertical' style={{ margin: 0, padding: 0 }}>
					{emails.map((email, index) => {
						const nameOfSender = email.Name
						const statusText = 'Sent'
						const recipients = email.EmailTo || ''
						const message = email.Comments ? email.Comments : ''
						const subject = `Service Ticket: ${service && service.Ticket ? service.Ticket : ''}`
						const sentDate = email.CreationDate
						const typeValue = email.type || email.Type
						const accountTypeLabel = getAccountTypeLabel(typeValue)

						return (
							<Step key={email.Token || index}>
								<StepLabel
									StepIconComponent={(iconProps) => <CustomEmailStepIcon {...iconProps} totalSteps={emails.length} />}
									onClick={(event) => onClickShowItem(event, index)}
									style={{ cursor: 'pointer' }}
								>
									<div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%' }}>
										<div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
											<Send style={{ fontSize: '16px', marginRight: '4px', color: '#666' }} />
											<Typography variant='subtitle2'>Email to: {recipients}</Typography>
											<Chip label={statusText} size='small' color={getStatusColor(statusText)} style={{ marginLeft: '8px' }} />
										</div>

										<Typography variant='body2' style={{ fontWeight: 'bold', marginBottom: '4px' }}>
											Subject: {subject}
										</Typography>

										<div style={{ width: '100%' }}>
											{activeStep === index ? (
												containsHtmlContent(message) ? (
													<div style={{ width: '100%' }} onClick={(event) => event.stopPropagation()}>
														<RichTextEditor name={`email-message-${email.Token || index}`} value={message} disabled />
													</div>
												) : (
													<Typography variant='body2' style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
														{message}
													</Typography>
												)
											) : (
												<Typography
													variant='body2'
													style={{
														overflow: 'hidden',
														textOverflow: 'ellipsis',
														display: '-webkit-box',
														WebkitLineClamp: 3,
														WebkitBoxOrient: 'vertical',
														wordBreak: 'break-word'
													}}
												>
													{formatEmailPreview(message)}
												</Typography>
											)}
										</div>
									</div>
								</StepLabel>

								<StepContent>
									<Card variant='outlined' style={{ marginTop: '8px' }}>
										<CardContent style={{ padding: '12px' }}>
											<Grid container spacing={1}>
												<Grid item xs={12}>
													<div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
														<Schedule style={{ fontSize: '16px', marginRight: '4px', color: '#666' }} />
														<Typography color='textSecondary' variant='caption'>
															{`Sent: ${moment(sentDate).format('MMM DD, YYYY - HH:mm')}`}
														</Typography>
													</div>
												</Grid>

												<Grid item xs={12}>
													<div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
														<Person style={{ fontSize: '16px', marginRight: '4px', color: '#666' }} />
														<Typography color='textSecondary' variant='caption'>
															Sent by: {nameOfSender}
														</Typography>
													</div>
												</Grid>
												<Grid item xs={12}>
													<div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
														<AccountBox style={{ fontSize: '16px', marginRight: '4px', color: '#666' }} />
														<Typography color='textSecondary' variant='caption'>
															Account Type: {accountTypeLabel}
														</Typography>
													</div>
												</Grid>

												{recipients && (
													<Grid item xs={12}>
														<Typography color='textSecondary' variant='caption'>
															<strong>Recipients:</strong> {recipients}
														</Typography>
													</Grid>
												)}

												{email.Attachments && email.Attachments.length > 0 && (
													<Grid item xs={12}>
														<div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
															<AttachFile style={{ fontSize: '16px', marginRight: '4px', color: '#666' }} />
															<Typography color='textSecondary' variant='caption'>
																<strong>Attachments:</strong>
															</Typography>
														</div>
														{email.Attachments.map((attachment) => (
															<Link
																key={attachment.Token}
																component='button'
																variant='caption'
																style={{ display: 'block', marginLeft: '20px', cursor: 'pointer' }}
																onClick={() => handleAttachmentClick(email.Token, attachment.Token)}
															>
																{attachment.FileName}
															</Link>
														))}
													</Grid>
												)}
											</Grid>
										</CardContent>
									</Card>
								</StepContent>
							</Step>
						)
					})}
				</Stepper>
			)}
		</div>
	)
}

export default TimeLineEmailHistory
